import { type FC, type MouseEventHandler, type ReactNode, useState, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import { Link } from 'dibs-elements/exports/Link';
import {
    BuyerIncentivesBanner,
    Incentives,
} from 'dibs-buyer-incentives/exports/BuyerIncentivesBanner';
import { usePdpDibsPromiseVariant } from '../../hooks/usePdpDibsPromiseVariant';
import { PdpSharedDibsPromiseModalLazy } from '../../PdpSharedModals/PdpSharedDibsPromiseModal/PdpSharedDibsPromiseModalLazy';

import { type PdpSharedSidebarBuyerIncentives_user$key } from './__generated__/PdpSharedSidebarBuyerIncentives_user.graphql';

const userFragment = graphql`
    fragment PdpSharedSidebarBuyerIncentives_user on User {
        ...PdpSharedDibsPromiseModalLazy_user
    }
`;

const PdpSharedSidebarBuyerIncentives: FC<{
    onDetailsClick?: MouseEventHandler;
    user?: PdpSharedSidebarBuyerIncentives_user$key | null | undefined;
    isPDPDesktopAggregationVariant?: boolean;
}> = ({ onDetailsClick, user: userRef, isPDPDesktopAggregationVariant = false }) => {
    const isDibsPromiseVariant = usePdpDibsPromiseVariant();
    const isVariant = isDibsPromiseVariant || isPDPDesktopAggregationVariant;
    const showLink = !!onDetailsClick || isDibsPromiseVariant;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const user = useFragment(userFragment, userRef);

    const handleOpenModal = useCallback(() => {
        setIsModalOpen(true);
        trackEvent({
            eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
            interaction_type: interactionTypeConstants.INTERACTION_TYPE_EBI_DETAILS,
            step_interaction_name:
                stepInteractionConstants.STEP_INTERACTION_EBI_SIDERAIL_DETAILS_CLICK,
        });
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const getLink = (chunk: ReactNode): ReactNode => {
        if (showLink) {
            return (
                <Link href="#" onClick={isVariant ? handleOpenModal : onDetailsClick}>
                    {chunk}
                </Link>
            );
        } else {
            return null;
        }
    };
    return (
        <>
            {!isVariant && (
                <span>
                    <FormattedMessage
                        id="PdpSharedSidebarBuyerIncentives.incentives.copy"
                        defaultMessage="<strong>The 1stDibs Promise:</strong> {incentives} <link>Details</link>"
                        values={{
                            strong: chunk => <strong>{chunk}</strong>,
                            incentives: <Incentives />,
                            link: chunk => getLink(chunk),
                        }}
                    />
                </span>
            )}

            {isVariant && (
                <>
                    <BuyerIncentivesBanner
                        linkPosition={isPDPDesktopAggregationVariant ? 'top' : 'bottom'}
                        getLink={getLink}
                    />
                    <PdpSharedDibsPromiseModalLazy
                        user={user}
                        isOpen={isModalOpen}
                        onClose={handleCloseModal}
                    />
                </>
            )}
        </>
    );
};

export default PdpSharedSidebarBuyerIncentives;
